// src/pages/commercial/CommercialAnalyticsPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import pb from '../../lib/pocketbase';
import { useAuth } from '../../contexts/AuthContext';

export default function CommercialAnalyticsPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        winRate: 0,
        totalDeals: 0,
        wonDeals: 0,
        lossReasons: {}
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchAnalytics() {
            try {
                // Fetch all CLOSED deals (won or lost)
                const deals = await pb.collection('deals').getFullList({
                    // Adjust filter based on your status values. Assuming 'won'/'lost' or stage names.
                    // Schema shows stage might be relations? Or status? 
                    // Let's assume standard 'won' and 'lost' status or probability=100 / 0
                    filter: `owner = "${user.id}" && (probability = 100 || probability = 0 || status = "won" || status = "lost")`
                });

                let won = 0;
                let lost = 0;
                const reasons = {};

                deals.forEach(d => {
                    const isWon = d.probability === 100 || d.status === 'won';
                    const isLost = d.probability === 0 || d.status === 'lost';

                    if (isWon) won++;
                    if (isLost) {
                        lost++;
                        const reason = d.loss_reason || 'non spécifié';
                        reasons[reason] = (reasons[reason] || 0) + 1;
                    }
                });

                const total = won + lost;
                setStats({
                    winRate: total ? Math.round((won / total) * 100) : 0,
                    totalDeals: total,
                    wonDeals: won,
                    lossReasons: reasons
                });

            } catch (err) {
                console.error("Error loading analytics", err);
            } finally {
                setLoading(false);
            }
        }
        fetchAnalytics();
    }, [user.id]);

    const maxReasonCount = Math.max(...Object.values(stats.lossReasons), 1);

    return (
        <CommercialLayout title="Statistiques & Performance 📊">
            <div className="commercial-page-wrapper">
                <style>{`
                .stats-grid {
                    display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 24px;
                    margin-bottom: 40px;
                }
                .stat-card {
                    background: var(--commercial-panel, #fff); padding: 24px; border-radius: 12px;
                    border: 1px solid var(--commercial-border, #e2e8f0);
                    display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;
                }
                .stat-value { font-size: 3rem; font-weight: 800; color: var(--commercial-text); }
                .stat-label { color: var(--commercial-muted); font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 8px; }
                
                .chart-container {
                    background: var(--commercial-panel, #fff); padding: 32px; border-radius: 12px;
                    border: 1px solid var(--commercial-border, #e2e8f0);
                }
                .bar-row {
                    display: flex; align-items: center; gap: 16px; margin-bottom: 16px;
                }
                .bar-label { width: 120px; text-align: right; font-weight: 500; }
                .bar-track { flex: 1; background: var(--commercial-panel-2, #f1f5f9); border-radius: 6px; height: 24px; overflow: hidden; }
                .bar-fill { height: 100%; background: #ef4444; border-radius: 6px; transition: width 1s ease-out; display: flex; align-items: center; padding-left: 8px; color: white; font-size: 0.8rem; font-weight: 600; }
                `}</style>

                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-value" style={{ color: stats.winRate > 30 ? '#10b981' : '#ef4444' }}>
                            {stats.winRate}%
                        </div>
                        <div className="stat-label">Taux de Conversion</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">{stats.wonDeals}</div>
                        <div className="stat-label">Deals Gagnés</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">{stats.totalDeals}</div>
                        <div className="stat-label">Total Clôturés</div>
                    </div>
                </div>

                <div className="chart-container">
                    <h2 style={{ marginTop: 0 }}>Pourquoi perdons-nous des deals ? 📉</h2>
                    <p style={{ color: 'var(--commercial-muted)', marginBottom: 32 }}>Analyse des causes d'échec sur {stats.totalDeals - stats.wonDeals} deals perdus.</p>

                    {Object.keys(stats.lossReasons).length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: 'var(--commercial-muted)' }}>
                            Pas assez de données pour le moment. Continuez à vendre (et à perdre un peu) ! 😉
                        </div>
                    ) : (
                        Object.entries(stats.lossReasons)
                            .sort(([, a], [, b]) => b - a)
                            .map(([reason, count]) => (
                                <div key={reason} className="bar-row">
                                    <div className="bar-label">{
                                        reason === 'price' ? 'Prix 💸' :
                                            reason === 'competitor' ? 'Concurrent ⚔️' :
                                                reason === 'features' ? 'Fonctionnalité 🛠️' :
                                                    reason === 'timing' ? 'Timing ⏱️' :
                                                        reason === 'ghosted' ? 'Silence 👻' : reason
                                    }</div>
                                    <div className="bar-track">
                                        <div className="bar-fill" style={{ width: `${(count / maxReasonCount) * 100}%` }}>
                                            {count}
                                        </div>
                                    </div>
                                </div>
                            ))
                    )}
                </div>
            </div>
        </CommercialLayout>
    );
}
